import React, { type PropsWithChildren, createContext, useContext, useMemo, useState } from 'react';

import { sortBy as _sortBy } from 'lodash';

import type { Invitation, InvitationWithFormResponses, SigninRequirements } from '@sine/sine-core-api-types';
import { type ISelectOption } from '@sine/sine-ui-components';

import { type PassWithResponseSummaries } from '../../activity/helpers/activityFeedCache.helpers';
import mapToDropdownOptions, {
  type ISelectOptionVisitorTypesRequirements,
  mapToDropdownOptionsWithVtRequirements,
} from '../../helpers/mapToDropdownOptions';
import { useSitesWithPermissionsQuery } from '../../services/site/site.hooks';
import { storage } from '../../services/storage/storage.service';
import { useInvitationDetailsQuery } from '../../shared/hooks/useInvitationDetailsQuery';
import { useSiteRequirementsQuery } from '../../shared/hooks/useSiteRequirementsQuery';
import { useZonesForHostProfileQuery } from '../../shared/hooks/useZonesQuery';
import { AclAction } from '../../team/permission/constants/actions';

export interface SiteOptionWithCountryCode extends ISelectOption {
  countryCode?: string;
}

type SigninContextType = {
  isLoading: boolean;
  setSelectedSite?: (value?: SiteOptionWithCountryCode | null) => void;
  setSelectedVisitorType?: (value?: ISelectOption | ISelectOptionVisitorTypesRequirements | null) => void;
  setSelectedHost?: (value?: ISelectOption | null) => void;
  setCheckInAnother?: (value?: boolean | undefined, event?: any) => void;
  siteRequirements?: SigninRequirements;
  checkInAnother?: boolean;
  siteOptions: Array<SiteOptionWithCountryCode>;
  hostOptions: Array<ISelectOption>;
  zoneOptions?: ZoneOptions;
  visitorTypeOptions: Array<ISelectOptionVisitorTypesRequirements>;
  invitationWithFormResponses?: InvitationWithFormResponses;
  selectedSite?: SiteOptionWithCountryCode | null;
  selectedVisitorType?: ISelectOption | ISelectOptionVisitorTypesRequirements | null;
  selectedHost?: ISelectOption | null;
  activeHostExclusions?: Array<string>;
  zonesEnabled: boolean;
};

const defaultSigninContext: SigninContextType = {
  isLoading: true,
  setSelectedSite: () => {},
  setSelectedVisitorType: () => {},
  setCheckInAnother: () => {},
  setSelectedHost: () => {},
  siteRequirements: undefined,
  checkInAnother: false,
  siteOptions: [],
  hostOptions: [],
  visitorTypeOptions: [],
  invitationWithFormResponses: undefined,
  selectedSite: undefined,
  selectedVisitorType: undefined,
  selectedHost: undefined,
  activeHostExclusions: [],
  zonesEnabled: false,
};

const SigninContext = createContext<SigninContextType>(defaultSigninContext);

interface ZoneOptions {
  values: Array<ISelectOption>;
  defaultOption: ISelectOption | null;
}

function getCheckInAnotherSetting() {
  const storageItem = storage.getItem('checkinAnother');

  if (!storageItem) {
    return false;
  }

  try {
    return JSON.parse(storageItem);
  } catch {
    storage.setItem('checkinAnother', 'false');
    return false;
  }
}

export default function SigninProvider({
  invitation,
  pass,
  children,
}: PropsWithChildren<{ invitation?: Invitation; pass?: PassWithResponseSummaries }>) {
  const [checkInAnother, setCheckInAnother] = useState<boolean | undefined>(false);
  const [selectedSite, setSelectedSite] = useState<SiteOptionWithCountryCode | undefined | null>();
  const [selectedVisitorType, setSelectedVisitorType] = useState<
    ISelectOption | ISelectOptionVisitorTypesRequirements | null | undefined
  >();
  const [selectedHost, setSelectedHost] = useState<ISelectOption | null | undefined>();

  const { isFetchingSites, sites } = useSitesWithPermissionsQuery([AclAction.SIGN_IN, AclAction.H_PROFILE_PASS]);

  const { siteOptions } = useMemo(() => {
    let options = sites
      .filter((site) => site.settings.hostRequiredForSignIn || site.permissions.includes(AclAction.SIGN_IN))
      .map<SiteOptionWithCountryCode>((site) => ({
        value: site.id,
        label: site.name,
        countryCode: site.countryCode,
      }));

    if (invitation) {
      // TODO: Same as above, siteId fallback shouldn't be necessary
      options = options.filter((site) => site.value === invitation.site || site.value === (invitation as any).siteId);
    }

    setCheckInAnother(getCheckInAnotherSetting());

    setSelectedSite(
      options.find((option) => option.value === pass?.site || option.value === invitation?.site) || options[0],
    );

    return { siteOptions: options };
  }, [sites, invitation, pass]);

  const { siteRequirements, isFetchingSiteRequirements } = useSiteRequirementsQuery(
    'sinein',
    selectedSite?.value as string | undefined,
  );

  const visitorTypeOptions = useMemo(
    () => mapToDropdownOptionsWithVtRequirements(siteRequirements?.visitorTypes),
    [siteRequirements],
  );

  const { invitationInfo: invitationWithFormResponses, isFetchingInvitation } = useInvitationDetailsQuery(
    invitation?.id,
  );

  const useHostOptionsAndExclusions = (
    _siteRequirements: SigninRequirements | undefined | null,
    selectedVisitorTypeOption: ISelectOption | null | undefined,
  ) => {
    const hostGroupExclusions = _siteRequirements?.hostGroupVisitorTypeExclusions;

    const { activeHostGroupIdExclusions, activeHostExclusions } = useMemo(() => {
      const _activeHostGroupIdExclusions = (hostGroupExclusions || [])
        .filter((exclusion) => exclusion.visitorTypeId === selectedVisitorTypeOption?.value)
        .map((exclusion) => exclusion.hostGroupId);

      const _activeHostExclusions = (_siteRequirements?.hostGroups || [])
        .filter((hostGroup) => _activeHostGroupIdExclusions.includes(hostGroup.id))
        .flatMap((hostGroup) => (hostGroup.hosts ? hostGroup.hosts.map((host) => host.id) : []));

      return { activeHostGroupIdExclusions: _activeHostGroupIdExclusions, activeHostExclusions: _activeHostExclusions };
    }, [hostGroupExclusions, selectedVisitorTypeOption?.value, _siteRequirements?.hostGroups]);

    const hostOptions = useMemo((): Array<ISelectOption> => {
      const hostsList = (_siteRequirements?.hostGroups || [])
        .filter((hostGroup) => !activeHostGroupIdExclusions.includes(hostGroup.id))
        .flatMap(
          (hostGroup) =>
            hostGroup.hosts?.map((host) => ({
              label: `${host.firstName} ${host.lastName} (${hostGroup.name})`,
              value: host.id,
            })),
        )
        .filter((host): host is { label: string; value: string } => host !== undefined);

      return _sortBy(hostsList, 'label');
    }, [activeHostGroupIdExclusions, _siteRequirements?.hostGroups]);

    return { hostOptions, activeHostExclusions };
  };

  const { hostOptions, activeHostExclusions } = useHostOptionsAndExclusions(siteRequirements, selectedVisitorType);

  const zonesEnabled = useMemo(
    () => !!(siteRequirements?.zonesEnabled && siteRequirements.defaultZoneId),
    [siteRequirements],
  );

  const { zonesForHostProfile, isFetchingZonesForHostProfile } = useZonesForHostProfileQuery(
    selectedSite?.value as string,
    selectedHost?.value as string,
  );

  const zoneOptions = useMemo(() => {
    if (!selectedSite?.value || !selectedHost?.value || !zonesEnabled || zonesForHostProfile.length === 0) {
      return { values: [], defaultOption: null };
    }

    const invitationZone = zonesForHostProfile.find((zone) => zone.id === pass?.zoneId);

    // TODO: refactor to use default zone per host group from site requirements when available
    const defaultZoneForHostGroup = zonesForHostProfile.find((zone) =>
      zone.hostGroups.find((zoneHostGroup) => zoneHostGroup.default),
    );

    const defaultZoneForSite = zonesForHostProfile.find((zone) => zone.default);

    const defaultZone = invitationZone || defaultZoneForHostGroup || defaultZoneForSite;

    return {
      values: mapToDropdownOptions(zonesForHostProfile),
      defaultOption: defaultZone ? { label: defaultZone.name, value: defaultZone.id } : null,
    };
  }, [selectedSite?.value, selectedHost?.value, zonesEnabled, zonesForHostProfile, pass?.zoneId]);

  const context = useMemo<SigninContextType>(
    () =>
      ({
        checkInAnother,
        siteOptions,
        hostOptions,
        visitorTypeOptions,
        isLoading:
          isFetchingSiteRequirements || isFetchingSites || isFetchingInvitation || isFetchingZonesForHostProfile,
        siteRequirements,
        invitationWithFormResponses,
        setSelectedSite,
        setSelectedVisitorType,
        selectedSite,
        selectedVisitorType,
        setCheckInAnother,
        activeHostExclusions,
        selectedHost,
        setSelectedHost,
        zoneOptions,
        zonesEnabled,
      }) satisfies SigninContextType,
    [
      checkInAnother,
      activeHostExclusions,
      siteOptions,
      hostOptions,
      isFetchingSiteRequirements,
      isFetchingSites,
      siteRequirements,
      setSelectedSite,
      setSelectedVisitorType,
      isFetchingInvitation,
      invitationWithFormResponses,
      selectedSite,
      selectedVisitorType,
      setCheckInAnother,
      visitorTypeOptions,
      isFetchingZonesForHostProfile,
      selectedHost,
      zoneOptions,
      setSelectedHost,
      zonesEnabled,
    ],
  );

  return <SigninContext.Provider value={context}>{children}</SigninContext.Provider>;
}

export function useSignIn() {
  return useContext(SigninContext);
}
